import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import DatePicker, { registerLocale } from 'react-datepicker'
import classNames from 'classnames'
import { arSA } from 'date-fns/locale'
import I18n from 'translations/i18n'
import { isRtl } from 'utils/uiHelpers'
import { format } from 'date-fns'

if (isRtl) {
  registerLocale('ar', arSA)
}

const ReactPicker = ({
  style,
  className,
  size,
  label,
  name,
  value,
  todayDate,
  message,
  error,
  dateFormat,
  displayFormat,
  placeholderText,
  locale,
  withClockIcon,
  hideTimeSelect,
  withCustomClearIcon,
  isPending,
  disabledTooltip,
  onChange,
  hideMonthDropdown,
  hideYearDropdown,
  testId,
  ...props
}) => {
  const [startDate, setStartDate] = React.useState()
  React.useEffect(() => {
    if (value) {
      setStartDate(new Date(value))
    } else {
      setStartDate(null)
    }
  }, [value])
  const today = todayDate || new Date()
  const handleOnChange = (val) => {
    if (!val) onChange('')
    onChange(format(new Date(val), dateFormat))
  }

  return (
    <div
      className={classNames(
        'jisr--datepicker-box',
        `jisr--datepicker-${size}`,
        withClockIcon ? 'with-clock-icon' : 'with-calendar-icon',
        hideTimeSelect && 'hide-time-select',
        withCustomClearIcon && 'with-custom-clear-icon',
        isPending && 'jisr--datepicker-pending',
        className,
      )}
      style={style}
    >
      {label && <label data-testid={`${testId}-label`}>{I18n.t(label)}</label>}
      <DatePicker
        data-testid={testId}
        calendarClassName="jisr--datepicker-calendar"
        className={classNames('jisr--datepicker-input', error && 'error')}
        dateFormat={displayFormat || dateFormat}
        name={name && name}
        placeholderText={I18n.t(placeholderText)}
        popperPlacement="bottom"
        locale={locale}
        selected={startDate}
        highlightDates={[{ 'react-datepicker__day--highlighted-custom-today': [today] }]}
        openToDate={startDate || today}
        renderDayContents={(day) => (
          <span
            className="day-content"
            data-testid={`day-content-${day}`}
          >
            {disabledTooltip && (
              <span className="day-tooltip day-tooltip__disabled">{disabledTooltip}</span>
            )}
            {day}
          </span>
        )}
        onChange={handleOnChange}
        popperModifiers={{
          offset: {
            enabled: true,
            offset: '0px, 10px',
          },
          preventOverflow: {
            enabled: true,
            escapeWithReference: false,
            boundariesElement: 'viewport',
          },
        }}
        {...(!hideMonthDropdown && { showMonthDropdown: true })}
        {...(!hideYearDropdown && { showYearDropdown: true })}
        {...props}
      />
      {error && <span className="jisr--datepicker-error">{error}</span>}
      {message && <small>{message}</small>}
    </div>
  )
}

ReactPicker.propTypes = {
  style: PropTypes.shape(),
  input: PropTypes.shape(),
  className: PropTypes.string,
  size: PropTypes.string,
  label: PropTypes.string,
  message: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string, PropTypes.number]),
  todayDate: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string, PropTypes.number]),
  error: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  dateFormat: PropTypes.string,
  displayFormat: PropTypes.string,
  placeholderText: PropTypes.string,
  locale: PropTypes.string,
  withClockIcon: PropTypes.bool,
  hideTimeSelect: PropTypes.bool,
  isPending: PropTypes.bool,
  withCustomClearIcon: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  onChange: PropTypes.func,
  hideMonthDropdown: PropTypes.bool,
  hideYearDropdown: PropTypes.bool,
  testId: PropTypes.string,
}

ReactPicker.defaultProps = {
  size: 'mideum',
  dateFormat: 'yyyy-MM-dd',
  placeholderText: I18n.t('select_date'),
  locale: 'ar', // prev
}

const mapStateToProps = ({ auth }) => ({ todayDate: auth.employeeDate })

export default React.memo(connect(mapStateToProps)(ReactPicker))
